'use client';

import { useEffect, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { apiFetch } from '@/lib/api-client';
import { useAuth } from '@/lib/use-auth';

/**
 * Referral redirect page — SPEC-037 C-07
 * Spec ref: SPEC-NEGOCIO-LOGICA-VIVA §3.1 (bono directo via referidos)
 *
 * Validates the referral code via GET /api/public/referral/:code
 * - Valid + not logged in → redirect to /register?ref=CODE
 * - Valid + logged in → redirect to /app?ref=CODE
 * - Invalid → 404 page (notFound per SPEC)
 */

// C-07 fix (R2 SPEC-037): Type the apiFetch response properly.
// apiFetch<T> returns Promise<T> (already parsed JSON + unwrapped from the
// { data: ... } envelope by handleResponse), NOT a raw Response.
//
// SPEC-038 R-1 / B-1 fix: previously the type was `{ data?: {...} }` and the
// caller checked `result?.data?.valid`. Since apiFetch already unwraps the
// envelope, `result.data` was always `undefined` → `notFound()` ALWAYS fired
// → every referral link 404'd. The type now reflects the unwrapped shape and
// the caller checks `result?.valid` directly.
interface ReferralValidationResponse {
  valid: boolean;
  // D-HIGH-001 fix (SPEC-044 QA AUDIT R1 — Batch 4): `username` tightened
  // from `username?: string` (optional) to `username: string` (required).
  // The backend's GET /public/referral/:code handler (public.controller.ts:79-84)
  // ALWAYS returns `username` when `valid: true` (it's selected via
  // `.select('username profile.name')` and sent in the response body as
  // `data.username`). The optional `?` was a defensive typing that masked
  // the contract — the spec instruction's literal code uses
  // `result.username` (no optional chaining), which would TS-error under
  // strict mode with `username?: string`. Tightening to `username: string`
  // makes the type match the actual backend behavior.
  username: string;
  displayName?: string;
}

export default function ReferralRedirectPage() {
  const params = useParams();
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const [error, setError] = useState(false);
  // H-06 fix: use a state variable instead of calling notFound() from
  // useEffect (which breaks Next.js rendering). notFound() must be
  // called during render, not as a side effect.
  const [notFoundState, setNotFoundState] = useState(false);

  // E-012 fix (SPEC-043 Batch E): depend on `user?.id` (primitive) instead of
  // the `user` object reference. The auth context may produce a new `user`
  // object on every auth state change (re-render) even when the underlying
  // user identity hasn't changed — depending on the whole object caused the
  // effect to re-fire on every render, re-fetching /public/referral/:code
  // and racing concurrent navigations.
  const userId = user?.id;

  useEffect(() => {
    if (authLoading) return; // wait for auth to resolve

    const code = params.code as string;
    if (!code) {
      setError(true);
      return;
    }

    // C-07 fix: apiFetch<ReferralValidationResponse> returns typed response directly.
    // No .ok/.json() needed — apiFetch already parses the response AND unwraps
    // the { data: ... } envelope (see handleResponse in api-client.ts).
    // SPEC-038 R-1 / B-1 fix: check `result?.valid` (NOT `result?.data?.valid`).
    apiFetch<ReferralValidationResponse>(`/public/referral/${encodeURIComponent(code)}`)
      .then((result) => {
        if (result?.valid) {
          // D-HIGH-001 + D-HIGH-002 fix (SPEC-044 QA AUDIT R1 — Batch 4):
          // Set the cross-session referral attribution cookie BEFORE
          // redirecting. Per SPEC-NEGOCIO §8.3 (Referral Attribution),
          // a referral click must be attributed even if the user doesn't
          // register immediately — the cookie persists across browser
          // sessions for 30 days (Max-Age=2592000) so when the user
          // eventually visits /register (with or without ?ref= in the
          // URL), the backend can read `profileia_ref` from the Cookie
          // header and attribute the registration to this referral code.
          //
          // Cookie attributes:
          //   - Path=/           → sent on every request to any path.
          //   - Max-Age=2592000   → 30 days (SPEC §8.3 attribution window).
          //   - SameSite=Lax      → sent on top-level navigations (the
          //                          /r/[code] → /register redirect is a
          //                          same-site navigation, so the cookie
          //                          is sent). NOT `Strict` because the
          //                          user might come from an external
          //                          link (e.g., WhatsApp share) and we
          //                          still want to attribute the visit.
          //   - Secure (prod only) → only sent over HTTPS (production).
          //                          4D-MED-001 fix: in dev
          //                          (http://localhost), the `Secure`
          //                          attribute caused the browser to
          //                          SILENTLY DROP the cookie (not just
          //                          the attribute — the entire cookie).
          //                          That broke dev-time QA of the
          //                          cross-session referral attribution
          //                          flow. Now we only emit `Secure` when
          //                          the protocol is HTTPS — matches
          //                          production behavior + keeps dev QA
          //                          working.
          //
          // Anti-§6 (CM-19): the cookie name `profileia_ref` matches the
          // backend's expected cookie name (read by the register
          // controller's referral-attribution middleware, which falls
          // back to `?ref=CODE` query param if the cookie is absent).
          const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
          document.cookie = `profileia_ref=${encodeURIComponent(code)}; Path=/; Max-Age=2592000; SameSite=Lax${isHttps ? '; Secure' : ''}`;

          // D-HIGH-001 fix: redirect LOGGED-IN users to the REFERRER's
          // public profile page (`/p/{username}`), not to `/app?ref=CODE`.
          // The original /app?ref=CODE was a placeholder that didn't do
          // anything useful for an already-authenticated user (the user
          // already has an account — they can't register again with this
          // referral code). Sending them to the referrer's public profile
          // is the correct UX: the user sees who referred them + can
          // share the referrer's profile / contact them. SPEC-NEGOCIO §8.3
          // (Referral Attribution — "logged-in user clicking a referral
          // link is taken to the referrer's profile, NOT to their own
          // dashboard").
          //
          // For LOGGED-OUT users: redirect to /register?ref=CODE so the
          // registration form pre-fills the `referralCode` field (see
          // register/page.tsx F-26 fix). The cookie set above is also
          // available as a fallback if the user closes the tab and
          // visits /register directly later (within 30 days).
          if (userId) {
            // SPEC-095 (P-146 fix): redirect to canonical /{username}
            router.replace(`/${encodeURIComponent(result.username)}`);
          } else {
            router.replace(`/register?ref=${code}`);
          }
        } else {
          setError(true);
        }
      })
      .catch(() => setError(true));
  }, [params.code, userId, authLoading]);

  // H-06 fix: set state instead of calling notFound() from useEffect.
  // notFound() will be called during render when notFoundState is true.
  useEffect(() => {
    if (error) setNotFoundState(true);
  }, [error]);

  // 4D-MED-006: Replace notFound() call with inline "Código de referido inválido"
  // UI + "Volver al inicio" link to avoid flash before 404.
  if (notFoundState) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="max-w-md text-center space-y-4 p-8">
          <div className="text-4xl">🔗</div>
          <h1 className="text-xl font-bold">Código de referido inválido</h1>
          <p className="text-muted-foreground">
            El código que intentaste usar no es válido o ha expirado.
          </p>
          <a
            href="/"
            className="inline-block rounded-lg bg-primary hover:bg-primary/90 px-4 py-2 text-sm font-medium text-primary-foreground transition-colors"
          >
            Volver al inicio
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <p className="text-muted-foreground">Verificando código de referido...</p>
    </div>
  );
}
